/**
 * The provenance-tagged summary value type (`summary-calculation` spec:
 * "Every summary value declares its provenance"). A bare
 * `{ value: 5 }` object literal does not satisfy `SummaryValue<number>`
 * — `provenance` is required, so a value can't be constructed without
 * declaring where it came from. See `provenance.test.ts` for the
 * enforced-at-typecheck-time proof.
 */
export type Provenance = 'original' | 'recalculated' | 'estimated' | 'edited' | 'unavailable'

export interface SummaryValue<T> {
  readonly value: T | undefined
  readonly provenance: Provenance
}

export function original<T>(value: T): SummaryValue<T> {
  return { value, provenance: 'original' }
}

export function recalculated<T>(value: T): SummaryValue<T> {
  return { value, provenance: 'recalculated' }
}

export function estimated<T>(value: T): SummaryValue<T> {
  return { value, provenance: 'estimated' }
}

/** A value the user typed in (an `editField` op), not read, measured or derived. */
export function edited<T>(value: T): SummaryValue<T> {
  return { value, provenance: 'edited' }
}

export function unavailable<T>(): SummaryValue<T> {
  return { value: undefined, provenance: 'unavailable' }
}
